/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { meta } from '../models/meta';
import type { TenantItem } from '../models/TenantItem';
import type { TenantRecordItem } from '../models/TenantRecordItem';
import { request as __request } from '../core/request';

export class TenantService {

    /**
     * Find tenant  by ID.
     * Returns matching record
     * @result TenantItem successful operation
     * @throws ApiError
     */
    public static async getTenantById({
        id,
    }: {
        /** Record ID **/
        id: number,
    }): Promise<TenantItem> {
        const result = await __request({
            method: 'GET',
            path: `/server/tenant/${id}`,
            errors: {
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Update an existing tenant
     * Update a record based on data given
     * @result TenantRecordItem Successful operation
     * @throws ApiError
     */
    public static async updateTenant({
        id,
        requestBody,
    }: {
        /** Record ID **/
        id: number,
        /** TenantData object that needs to be updated **/
        requestBody: TenantRecordItem,
    }): Promise<TenantRecordItem> {
        const result = await __request({
            method: 'PUT',
            path: `/server/tenant/${id}`,
            body: requestBody,
            errors: {
                400: `Error: Update Failed`,
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Find multiple tenants.  Filterable by name, dates, etc
     * Returns one or more matching records
     * @result any successful operation
     * @throws ApiError
     */
    public static async getTenantList({
        name,
        userId,
        createdBy,
        updatedBy,
        createdStart,
        createdEnd,
        updatedStart,
        updatedEnd,
        activeOnly = true,
        userActiveInclude = 'all',
        page = 1,
        pageSize = 10,
        sort,
    }: {
        /** Find records searching by Name.  Enter ID for specific record **/
        name?: string,
        /** Find records associated with a given User **/
        userId?: number,
        /** Find records created by a given User **/
        createdBy?: number,
        /** Find records updated by a given User **/
        updatedBy?: number,
        /** Start time for range of record creation **/
        createdStart?: number,
        /** End time for range of record creation **/
        createdEnd?: number,
        /** Start time for range of record creation **/
        updatedStart?: number,
        /** End time for range of record creation **/
        updatedEnd?: number,
        /** Records to Include based on Active Flag. **/
        activeOnly?: boolean,
        /** Records to Include based on User Active Flag.  Allows for inactive only if desired. **/
        userActiveInclude?: 'all' | 'active' | 'inactive',
        /** Page to begin results at **/
        page?: number,
        /** Number of results per page **/
        pageSize?: number,
        /** Sort Options - Comma delimited list of key:order. order optional - default ASC  **/
        sort?: string,
    }): Promise<{
        data?: Array<TenantRecordItem>,
        meta?: meta,
    }> {
        const result = await __request({
            method: 'GET',
            path: `/server/tenant/list`,
            query: {
                'name': name,
                'userId': userId,
                'createdBy': createdBy,
                'updatedBy': updatedBy,
                'createdStart': createdStart,
                'createdEnd': createdEnd,
                'updatedStart': updatedStart,
                'updatedEnd': updatedEnd,
                'activeOnly': activeOnly,
                'userActiveInclude': userActiveInclude,
                'page': page,
                'pageSize': pageSize,
                'sort': sort,
            },
            errors: {
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Create a new tenant
     * Creates a record based on data given
     * @result TenantRecordItem Successful operation
     * @throws ApiError
     */
    public static async createTenant({
        requestBody,
    }: {
        /** TenantData object to be created **/
        requestBody: TenantRecordItem,
    }): Promise<TenantRecordItem> {
        const result = await __request({
            method: 'POST',
            path: `/server/tenant`,
            body: requestBody,
            errors: {
                400: `Error: Update Failed`,
                403: `Error: Forbidden`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Add a user to a tenant
     * Creates a record based on data given
     * @result any Successful operation
     * @throws ApiError
     */
    public static async addTenantUser({
        id,
        userIdPath,
    }: {
        /** Record ID **/
        id: number,
        /** User ID **/
        userIdPath: number,
    }): Promise<any> {
        const result = await __request({
            method: 'POST',
            path: `/server/tenant/${id}/user/${userIdPath}`,
            errors: {
                400: `Error: Bad Request`,
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

    /**
     * Remove an user from a tenant
     * Remove a record based on data given
     * @result any Successful operation
     * @throws ApiError
     */
    public static async deleteTenantUser({
        id,
        userIdPath,
    }: {
        /** Record ID **/
        id: number,
        /** User ID **/
        userIdPath: number,
    }): Promise<any> {
        const result = await __request({
            method: 'DELETE',
            path: `/server/tenant/${id}/user/${userIdPath}`,
            errors: {
                403: `Error: Forbidden`,
                404: `Error: Not Found`,
                422: `Error: Unprocessable Entity`,
            },
        });
        return result.body;
    }

}